import { useEffect, useState } from "react";
import { Icon } from "./icons";
import { DISMISSED_KEY, UPDATE_POLL_MS, bannerFor, fetchRelease, type UpdateBanner as Banner } from "../lib/update";

const readDismissed = () => { try { return localStorage.getItem(DISMISSED_KEY); } catch { return null; } };

/**
 * The "Model Bench has been updated" bar under the toolbar. Checks on sign-in, every few minutes, and when the tab
 * comes back into view. Web only: the desktop app is updated by its installer.
 */
export function UpdateBanner({ onRefresh }: { onRefresh: () => void }) {
  const [banner, setBanner] = useState<Banner | null>(null);

  useEffect(() => {
    if (__BUILD_ID__ === "dev") return;
    let alive = true;
    const check = async () => {
      const served = await fetchRelease();
      if (alive) setBanner(bannerFor({ build: __BUILD_ID__, notesId: __NOTES_ID__ }, served, readDismissed()));
    };
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    void check();
    const timer = window.setInterval(check, UPDATE_POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => { alive = false; window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  if (!banner) return null;
  const dismiss = () => { try { localStorage.setItem(DISMISSED_KEY, banner.build); } catch { /* storage unavailable */ } setBanner(null); };
  return (
    <div role="status" className="update-banner" data-testid="update-banner">
      <div style={{ flex: 1 }}>
        <b>{banner.notes ? `Model Bench has been updated — ${banner.notes.title}` : "Model Bench has been updated."}</b>
        {banner.notes && banner.notes.items.length > 0 && (
          <ul data-testid="update-notes">{banner.notes.items.map((item) => <li key={item}>{item}</li>)}</ul>
        )}
        <div className="muted">Refresh the page to start using the new version. Your work is saved first.</div>
      </div>
      <button className="btn primary" onClick={onRefresh} data-testid="update-refresh"><Icon name="refresh" />Refresh now</button>
      <button className="btn flat" onClick={dismiss} title="Hide until the next update" aria-label="Hide until the next update" data-testid="update-dismiss"><Icon name="x" /></button>
    </div>
  );
}
